import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auditApi, authApi, adminApi, catalogApi, hasCapability, labelApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLabelContext } from "@/contexts/LabelContext";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { AUDIT } from "@/constants/testIds";
import { Shield, Activity, Users, Building2, Plus, X, Loader2, RefreshCw, Settings, MessageSquare, FileSpreadsheet, MessageCircle, Copy, CheckCircle, RotateCcw, Search, Edit3, UserX, UserCheck, Clock, Calendar, History, ChevronDown, AlertTriangle, Tag } from "lucide-react";
import RolePackEditor from "./RolePackEditor";
import WhatsAppChannelSetup from "./WhatsAppChannelSetup";
import CommunicationsAdmin from "./CommunicationsAdmin";
import ConciergeAdminPanel from "@/components/ConciergeAdminPanel";

// LCM-01: Lifecycle status badge using Prashasta brand tokens
const STATE_COLORS = {
  ACTIVE:     { bg: "#F0FDF4", border: "#86EFAC", text: "#166534" },
  INVITED:    { bg: "#FFF7ED", border: "#FED7AA", text: "#92400E" },
  INACTIVE:   { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B" },
  SUSPENDED:  { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B" },
  EXPIRED:    { bg: "#F3F4F6", border: "#D1D5DB", text: "#6B7280" },
  LOCKED:     { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B" },
  DISABLED:   { bg: "#F3F4F6", border: "#D1D5DB", text: "#6B7280" },
};
const LcmStateBadge = ({ state }) => {
  const c = STATE_COLORS[state] || STATE_COLORS.INACTIVE;
  return (
    <span style={{ background: c.bg, borderColor: c.border, color: c.text }}
      className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded border">
      {state || "UNKNOWN"}
    </span>
  );
};

export default function Administration() {  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine default tab from URL path
  const defaultTab =
    location.pathname.includes("/users") ? "users" :
    location.pathname.includes("/audit") ? "audit" :
    "audit";

  const [tab, setTab] = useState(defaultTab);
  const [auditEvents, setAuditEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [invitationResult, setInvitationResult] = useState(null); // { activation_url, email, ... }

  // LCM-01 Users tab state
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("");
  const [editingUser, setEditingUser] = useState(null);      // user object for Edit modal
  const [deactivatingUser, setDeactivatingUser] = useState(null); // user for Deactivate dialog
  const [deactivateReason, setDeactivateReason] = useState("");
  const [auditUser, setAuditUser] = useState(null);          // user for audit panel
  const [userAuditData, setUserAuditData] = useState(null);
  const [userAuditLoading, setUserAuditLoading] = useState(false);
  const [lcmLoading, setLcmLoading] = useState(false);
  // Edit form state
  const [editForm, setEditForm] = useState({});
  const [catalogData, setCatalogData] = useState(null);
  const [rolePacksData, setRolePacksData] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [permView, setPermView] = useState("roles"); // "roles" | "catalog"
  const [expandedRole, setExpandedRole] = useState(null);
  // P-ARC-02 — Role pack editor (slide-in panel)
  const [editingRoleId, setEditingRoleId] = useState(null);

  const loadAudit = () => auditApi.listAudit({ limit: 100 }).then(r => setAuditEvents(r.data.results || []));
  const loadUsers = () => authApi.listUsers().then(r => setUsers(r.data));

  // LCM-01: helper actions
  const openAudit = async (u) => {
    setAuditUser(u);
    setUserAuditLoading(true);
    try {
      const r = await authApi.getUserAudit(u.id);
      setUserAuditData(r.data);
    } catch { setUserAuditData(null); }
    finally { setUserAuditLoading(false); }
  };

  const handleDeactivate = async () => {
    if (!deactivatingUser || !deactivateReason.trim()) return;
    setLcmLoading(true);
    try {
      await authApi.deactivateUser(deactivatingUser.id, { reason: deactivateReason });
      toast.success(`${deactivatingUser.name || deactivatingUser.email} deactivated`);
      setDeactivatingUser(null); setDeactivateReason("");
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivation failed");
    } finally { setLcmLoading(false); }
  };

  const handleReactivate = async (u) => {
    setLcmLoading(true);
    try {
      await authApi.reactivateUser(u.id);
      toast.success(`${u.name || u.email} reactivated`);
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivation failed");
    } finally { setLcmLoading(false); }
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    setLcmLoading(true);
    try {
      await authApi.editUser(editingUser.id, editForm);
      toast.success("User updated");
      setEditingUser(null); setEditForm({});
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setLcmLoading(false); }
  };

  // LCM-01: filtered users for display
  const filteredUsers = (users || []).filter(u => {
    const q = userSearch.toLowerCase();
    const matchSearch = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    const matchRole = !userRoleFilter || u.role === userRoleFilter;
    const matchStatus = !userStatusFilter || u.user_state === userStatusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  const TENANT_ROLES = [
    "BUSINESS_OWNER","TENANT_ADMIN","CRM_MANAGER","RELATIONSHIP_MANAGER",
    "EVENT_PLANNER_DESIGNER","PROJECT_MANAGER","OPERATIONS_COORDINATOR",
    "FINANCE_APPROVER","PRASHASTA_E2E_COORDINATOR",
  ];
  const loadTenants = () => adminApi.listTenants().then(r => setTenants(r.data));

  const loadCatalog = async () => {
    if (catalogData && rolePacksData) return; // already loaded
    setCatalogLoading(true);
    try {
      const [catRes, packsRes] = await Promise.all([
        catalogApi.listCapabilityCatalog(),
        catalogApi.listRolePacks(),
      ]);
      setCatalogData(catRes.data);
      setRolePacksData(packsRes.data);
    } catch {
      toast.error("Could not load capability catalog");
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([loadAudit(), loadUsers(), loadTenants()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "permissions") loadCatalog();
  }, [tab]);

  const TABS = [
    { id: "audit",       label: "Audit Trail",   icon: Activity      },
    { id: "concierge",   label: "Concierge",     icon: MessageCircle },
    { id: "users",       label: "Users",         icon: Users         },
    { id: "tenants",     label: "Tenants",       icon: Building2     },
    { id: "permissions", label: "Permissions",   icon: Shield        },
    { id: "comms",       label: "Communications",icon: MessageSquare },
    { id: "data",        label: "Data Imports",  icon: FileSpreadsheet },
    { id: "terminology", label: "Terminology",   icon: Tag           },
  ];

  if (loading) return <div className="p-6 animate-pulse space-y-4"><div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/><div className="h-64 bg-[#F3F3F3] rounded-xl"/></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0176D3]" />
            <h1 className="text-2xl font-bold text-[#181818] font-sans">Administration</h1>
          </div>
          <p className="text-sm text-[#706E6B] mt-1">Users, tenants and audit trail</p>
        </div>
        <button onClick={() => { loadAudit(); loadUsers(); }}
          className="p-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => t.id === "data" ? navigate("/administration/imports") : setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              tab === t.id ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "text-[#706E6B] hover:text-[#181818]"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Audit Trail */}
      {tab === "audit" && (
        <div data-testid={AUDIT.auditList} className="space-y-2">
          <p className="text-xs font-mono text-[#706E6B]">{auditEvents.length} audit records</p>
          {auditEvents.map(ev => (
            <div key={ev.id} data-testid={`${AUDIT.auditItem}-${ev.id}`}
              className="flex items-start gap-4 p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-[#EEF4FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Activity className="w-3.5 h-3.5 text-[#0176D3]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-bold text-[#0176D3]">{ev.action}</span>
                  <span className="text-[10px] font-mono text-[#706E6B] bg-[#F3F3F3] px-1.5 py-0.5 rounded border border-[#E5E5E5]">{ev.entity_type}</span>
                </div>
                <p className="text-sm text-[#444444] mt-0.5">{ev.summary}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-[#706E6B]">
                  <span>{ev.actor_email || "system"}</span>
                  <span>·</span>
                  <span>{ev.source}</span>
                  <span>·</span>
                  <span>{new Date(ev.timestamp).toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
          {auditEvents.length === 0 && <Empty msg="No audit events" />}
        </div>
      )}

      {/* ── LCM-01: Enhanced Users tab ──────────────────────────────────── */}
      {tab === "users" && (
        <div className="space-y-4" data-testid="users-panel">

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-1 flex-wrap">
              {/* Search */}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[#706E6B]" />
                <input
                  data-testid="user-search-input"
                  placeholder="Search name or email…"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-[#EDE8DE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30 text-[#1C1917]"
                />
              </div>
              {/* Role filter */}
              <select
                data-testid="user-role-filter"
                value={userRoleFilter}
                onChange={e => setUserRoleFilter(e.target.value)}
                className="px-3 py-2 text-sm bg-white border border-[#EDE8DE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30 text-[#1C1917]"
              >
                <option value="">All Roles</option>
                {TENANT_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
              </select>
              {/* Status filter */}
              <select
                data-testid="user-status-filter"
                value={userStatusFilter}
                onChange={e => setUserStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm bg-white border border-[#EDE8DE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30 text-[#1C1917]"
              >
                <option value="">All Statuses</option>
                {["ACTIVE","INVITED","INACTIVE","SUSPENDED","EXPIRED"].map(s =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs font-mono text-[#706E6B]">{filteredUsers.length} / {(users||[]).length}</span>
              {hasCapability(user, "admin.users") && (
                <button data-testid="invite-user-btn" onClick={() => setShowCreateUser(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: "#7A1A2B" }}>
                  <Plus className="w-3.5 h-3.5" /> Invite User
                </button>
              )}
            </div>
          </div>

          {/* User rows */}
          <div className="space-y-2">
            {filteredUsers.map(u => {
              const isInactive = ["INACTIVE","SUSPENDED","DISABLED","EXPIRED"].includes(u.user_state);
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`}
                  className="border rounded-xl overflow-hidden"
                  style={{ background: "#FAF6F0", borderColor: "#EDE8DE" }}>
                  {/* Main row */}
                  <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
                      style={{ background: isInactive ? "#9CA3AF" : "#7A1A2B" }}>
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </div>
                    {/* Identity */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1C1917]">{u.name}</p>
                      <p className="text-xs font-mono text-[#706E6B] truncate">{u.email}</p>
                    </div>
                    {/* Role */}
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded border" style={{ color: "#7A1A2B", borderColor: "#C9A15F", background: "#FAF6F0" }}>
                      {u.role?.replace(/_/g," ") || "—"}
                    </span>
                    {/* Status */}
                    <LcmStateBadge state={u.user_state} />
                    {/* Dates */}
                    <div className="hidden md:flex items-center gap-3 text-[10px] font-mono text-[#706E6B]">
                      {u.access_start_date && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />From {u.access_start_date}</span>
                      )}
                      {u.access_end_date && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Until {u.access_end_date}</span>
                      )}
                      {u.last_login_at && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Login {new Date(u.last_login_at).toLocaleDateString()}</span>
                      )}
                    </div>
                    {/* Actions */}
                    {hasCapability(user, "admin.users") && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button data-testid={`edit-user-${u.id}`} title="Edit user"
                          onClick={() => { setEditingUser(u); setEditForm({ name: u.name||"", email: u.email, role: u.role, access_start_date: u.access_start_date||"", access_end_date: u.access_end_date||"" }); }}
                          className="p-1.5 rounded-lg border border-[#EDE8DE] hover:border-[#C9A15F] text-[#706E6B] hover:text-[#7A1A2B] transition-all">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        {isInactive ? (
                          <button data-testid={`reactivate-user-${u.id}`} title="Reactivate"
                            onClick={() => handleReactivate(u)} disabled={lcmLoading}
                            className="p-1.5 rounded-lg border border-[#EDE8DE] hover:border-green-400 text-[#706E6B] hover:text-green-700 transition-all">
                            <UserCheck className="w-3.5 h-3.5" />
                          </button>
                        ) : u.user_state !== "INVITED" && (
                          <button data-testid={`deactivate-user-${u.id}`} title="Deactivate"
                            onClick={() => { setDeactivatingUser(u); setDeactivateReason(""); }}
                            className="p-1.5 rounded-lg border border-[#EDE8DE] hover:border-red-400 text-[#706E6B] hover:text-red-700 transition-all">
                            <UserX className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button data-testid={`audit-user-${u.id}`} title="View history"
                          onClick={() => openAudit(u)}
                          className="p-1.5 rounded-lg border border-[#EDE8DE] hover:border-[#7A1A2B]/40 text-[#706E6B] hover:text-[#7A1A2B] transition-all">
                          <History className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Mobile date row */}
                  {(u.access_start_date || u.access_end_date || u.last_login_at) && (
                    <div className="flex md:hidden items-center gap-3 px-4 py-2 border-t text-[10px] font-mono text-[#706E6B]" style={{ borderColor: "#EDE8DE", background: "#F7F3EE" }}>
                      {u.access_start_date && <span>From {u.access_start_date}</span>}
                      {u.access_end_date && <span>Until {u.access_end_date}</span>}
                      {u.last_login_at && <span>Last login {new Date(u.last_login_at).toLocaleDateString()}</span>}
                    </div>
                  )}
                </div>
              );
            })}
            {filteredUsers.length === 0 && <Empty msg="No users match the current filters" />}
          </div>

          {/* ── Edit User Modal ──────────────────────────────────────────── */}
          {editingUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="edit-user-modal">
              <div className="w-full max-w-md mx-4 rounded-2xl shadow-xl border" style={{ background: "#FAF6F0", borderColor: "#EDE8DE" }}>
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EDE8DE" }}>
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4" style={{ color: "#7A1A2B" }} />
                    <h2 className="text-base font-semibold text-[#1C1917]">Edit User</h2>
                  </div>
                  <button onClick={() => { setEditingUser(null); setEditForm({}); }} className="p-1 rounded text-[#706E6B] hover:text-[#1C1917]"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-[#706E6B] block mb-1">Full Name</label>
                    <input data-testid="edit-user-name" value={editForm.name||""} onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#706E6B] block mb-1">Email</label>
                    <input data-testid="edit-user-email" type="email" value={editForm.email||""} onChange={e => setEditForm(f => ({...f, email: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#706E6B] block mb-1">Role</label>
                    <select data-testid="edit-user-role" value={editForm.role||""} onChange={e => setEditForm(f => ({...f, role: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }}>
                      {TENANT_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#706E6B] block mb-1">Access Start Date</label>
                      <input data-testid="edit-user-start-date" type="date" value={editForm.access_start_date||""} onChange={e => setEditForm(f => ({...f, access_start_date: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#706E6B] block mb-1">Access End Date</label>
                      <input data-testid="edit-user-end-date" type="date" value={editForm.access_end_date||""} onChange={e => setEditForm(f => ({...f, access_end_date: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }} />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#706E6B]">Leave date fields blank to clear the restriction. Every change is recorded in the audit trail.</p>
                </div>
                <div className="flex gap-2 px-5 py-4 border-t" style={{ borderColor: "#EDE8DE" }}>
                  <button onClick={() => { setEditingUser(null); setEditForm({}); }}
                    className="flex-1 py-2 text-sm rounded-lg border font-medium text-[#706E6B] hover:bg-[#EDE8DE] transition-colors" style={{ borderColor: "#EDE8DE" }}>Cancel</button>
                  <button data-testid="save-user-edit" onClick={handleEditSave} disabled={lcmLoading}
                    className="flex-1 py-2 text-sm rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: "#7A1A2B" }}>
                    {lcmLoading ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Deactivate Dialog ──────────────────────────────────────────── */}
          {deactivatingUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="deactivate-user-modal">
              <div className="w-full max-w-sm mx-4 rounded-2xl shadow-xl border" style={{ background: "#FAF6F0", borderColor: "#EDE8DE" }}>
                <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "#EDE8DE" }}>
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <h2 className="text-base font-semibold text-[#1C1917]">Deactivate User</h2>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <p className="text-sm text-[#444]">Deactivating <strong>{deactivatingUser.name}</strong> will immediately revoke all active sessions and prevent login.</p>
                  <div>
                    <label className="text-xs font-medium text-[#706E6B] block mb-1">Reason <span className="text-red-500">*</span></label>
                    <textarea data-testid="deactivate-reason" value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)}
                      rows={3} placeholder="Enter deactivation reason…"
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white resize-none focus:outline-none focus:ring-2 focus:ring-[#7A1A2B]/30" style={{ borderColor: "#EDE8DE" }} />
                  </div>
                </div>
                <div className="flex gap-2 px-5 py-4 border-t" style={{ borderColor: "#EDE8DE" }}>
                  <button onClick={() => { setDeactivatingUser(null); setDeactivateReason(""); }}
                    className="flex-1 py-2 text-sm rounded-lg border font-medium text-[#706E6B] hover:bg-[#EDE8DE] transition-colors" style={{ borderColor: "#EDE8DE" }}>Cancel</button>
                  <button data-testid="confirm-deactivate" onClick={handleDeactivate} disabled={!deactivateReason.trim() || lcmLoading}
                    className="flex-1 py-2 text-sm rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 bg-red-700">
                    {lcmLoading ? "Deactivating…" : "Deactivate"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Audit History Panel ──────────────────────────────────────── */}
          {auditUser && (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" data-testid="user-audit-panel">
              <div className="w-full sm:max-w-xl mx-0 sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-xl border max-h-[80vh] flex flex-col" style={{ background: "#FAF6F0", borderColor: "#EDE8DE" }}>
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EDE8DE" }}>
                  <div className="flex items-center gap-2">
                    <History className="w-4 h-4" style={{ color: "#7A1A2B" }} />
                    <h2 className="text-base font-semibold text-[#1C1917]">{auditUser.name} — Audit History</h2>
                  </div>
                  <button onClick={() => { setAuditUser(null); setUserAuditData(null); }} className="p-1 rounded text-[#706E6B]"><X className="w-4 h-4" /></button>
                </div>
                <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
                  {userAuditLoading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#7A1A2B" }} /></div>}
                  {!userAuditLoading && userAuditData && (
                    <>
                      <p className="text-[10px] font-mono text-[#706E6B] uppercase tracking-widest mb-2">Identity Events</p>
                      {(userAuditData.identity_events || []).map((ev, i) => (
                        <div key={i} data-testid={`user-audit-event-${i}`}
                          className="flex items-start gap-3 p-3 rounded-xl border" style={{ borderColor: "#EDE8DE", background: "white" }}>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: "#F4E8EB" }}>
                            <Activity className="w-3 h-3" style={{ color: "#7A1A2B" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-mono font-bold" style={{ color: "#7A1A2B" }}>{ev.action}</span>
                            <p className="text-xs text-[#444] mt-0.5">{ev.summary}</p>
                            <p className="text-[10px] font-mono text-[#706E6B] mt-0.5">{new Date(ev.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                      {(userAuditData.identity_events || []).length === 0 && <p className="text-xs text-[#706E6B]">No identity events recorded.</p>}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tenants */}
      {tab === "tenants" && (
        <div className="space-y-3">
          <p className="text-xs font-mono text-[#706E6B]">{tenants.length} tenant(s)</p>
          {tenants.map(t => (
            <div key={t.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-[#181818]">{t.name}</h3>
                  <span className="text-xs font-mono text-[#706E6B]">{t.tenant_number}</span>
                </div>
                <p className="text-xs text-[#706E6B] mt-0.5">{t.country} · {t.timezone}</p>
              </div>
              <StatusBadge status={t.status} />
            </div>
          ))}
          {tenants.length === 0 && <Empty msg="No tenants found" />}
        </div>
      )}

      {/* Permissions — P-ARC-01 read-only inspection */}
      {tab === "permissions" && (
        <div className="space-y-4" data-testid="permissions-panel">
          {/* Header row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs font-mono text-[#706E6B]">
                Platform-default role packs · Catalog v{catalogData?.catalog_version ?? "—"}
              </p>
              <p className="text-[10px] font-mono text-[#706E6B] mt-0.5">
                {hasCapability(user, "admin.role_packs.manage")
                  ? "Configurable roles support bounded permission customisation · Changes take effect immediately"
                  : "Configurable roles can be edited by a Tenant Admin with 'Manage Role Pack Customisations' permission"}
              </p>
            </div>
            {/* View toggle */}
            <div className="flex items-center gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg p-0.5">
              {[["roles", "Role Packs"], ["catalog", "Capability Catalog"]].map(([v, label]) => (
                <button key={v} onClick={() => setPermView(v)} data-testid={`perm-view-${v}`}
                  className={`px-3 py-1 rounded text-xs font-mono transition-colors ${permView === v ? "bg-white shadow text-[#181818] font-semibold" : "text-[#706E6B] hover:text-[#181818]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {catalogLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" />
            </div>
          )}

          {/* ── Role Packs view ──────────────────────────────────────────── */}
          {!catalogLoading && permView === "roles" && rolePacksData && (
            <div className="space-y-2" data-testid="role-packs-list">
              {rolePacksData.role_packs.map(pack => (
                <div key={pack.role_id} className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={`role-pack-${pack.role_id}`}>
                  {/* Role header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#F3F3F3] hover:bg-[#EAEAEA] transition-colors">
                    <button
                      className="flex items-center gap-3 flex-1 text-left"
                      onClick={() => setExpandedRole(expandedRole === pack.role_id ? null : pack.role_id)}
                    >
                      <Shield className="w-3.5 h-3.5 text-[#0176D3] flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-[#181818] font-sans">{pack.display_name}</p>
                        <p className="text-xs font-mono text-[#706E6B]">{pack.role_id}</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] font-mono text-[#706E6B] bg-white border border-[#E5E5E5] px-2 py-0.5 rounded">
                        {pack.effective_count} effective
                      </span>
                      <ScopePill scope={pack.scope} />
                      {hasCapability(user, "admin.role_packs.manage") && pack.configurable && (
                        <button
                          onClick={() => setEditingRoleId(pack.role_id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#5E4DB2] text-white text-[11px] font-medium hover:bg-[#4C3B99] transition-colors"
                          data-testid={`edit-role-pack-${pack.role_id}`}
                        >
                          <Settings className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedRole(expandedRole === pack.role_id ? null : pack.role_id)}
                        className="text-[#706E6B] text-xs p-1"
                        aria-label="toggle"
                      >
                        {expandedRole === pack.role_id ? "▲" : "▼"}
                      </button>
                    </div>
                  </div>

                  {/* Capability detail */}
                  {expandedRole === pack.role_id && (
                    <div className="px-4 py-3 bg-white border-t border-[#E5E5E5] space-y-3" data-testid={`role-pack-detail-${pack.role_id}`}>
                      <p className="text-xs text-[#706E6B]">{pack.description}</p>

                      {pack.capability_patterns.length > 0 && (
                        <div>
                          <p className="text-[10px] font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">Wildcard Patterns</p>
                          <div className="flex flex-wrap gap-1.5">
                            {pack.capability_patterns.map(p => (
                              <span key={p} className="text-[10px] font-mono bg-[#FFF3E0] border border-orange-200 text-orange-700 px-2 py-0.5 rounded">
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {pack.capability_ids.length > 0 && (
                        <div>
                          <p className="text-[10px] font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">Explicit Capabilities</p>
                          <div className="flex flex-wrap gap-1.5">
                            {pack.capability_ids.map(c => (
                              <span key={c} className="text-[10px] font-mono bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] px-2 py-0.5 rounded">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="text-[10px] font-mono text-[#706E6B]">Pack version: {pack.pack_version} · Last updated: {pack.updated_at ? new Date(pack.updated_at).toLocaleDateString() : "—"}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Catalog view ─────────────────────────────────────────────── */}
          {!catalogLoading && permView === "catalog" && catalogData && (
            <div className="space-y-4" data-testid="capability-catalog-view">
              <p className="text-xs font-mono text-[#706E6B]">{catalogData.total_capabilities} capabilities across {catalogData.groups.length} groups</p>
              {catalogData.groups.map(group => (
                <div key={group.group} className="border border-[#E5E5E5] rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#F3F3F3] border-b border-[#E5E5E5] flex items-center justify-between">
                    <p className="text-xs font-semibold text-[#181818] font-sans">{group.group}</p>
                    <span className="text-[10px] font-mono text-[#706E6B]">{group.capabilities.length} caps</span>
                  </div>
                  <div className="divide-y divide-[#F3F3F3]">
                    {group.capabilities.map(cap => (
                      <div key={cap.id} className="px-4 py-2.5 bg-white flex items-start justify-between gap-3" data-testid={`cap-${cap.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-[#181818] font-semibold">{cap.id}</p>
                          <p className="text-xs text-[#706E6B] mt-0.5">{cap.display_name} · {cap.description}</p>
                        </div>
                        <RiskPill risk={cap.risk_level} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreateUser && (
        <InviteUserModal
          onClose={() => { setShowCreateUser(false); setInvitationResult(null); }}
          onSuccess={(result) => { setShowCreateUser(false); setInvitationResult(result); loadUsers(); }}
        />
      )}
      {invitationResult && (
        <ActivationLinkModal
          result={invitationResult}
          onClose={() => setInvitationResult(null)}
        />
      )}
      {editingRoleId && (
        <RolePackEditor
          roleId={editingRoleId}
          onClose={() => setEditingRoleId(null)}
        />
      )}

      {/* Communications — COM-01 Unified Omnichannel Workspace */}
      {tab === "comms" && (
        <div className="space-y-1" data-testid="comms-panel">
          <CommunicationsAdmin />
        </div>
      )}

      {/* CONCIERGE-01: Smart Conversational Enquiry Assistant */}
      {tab === "concierge" && (
        <div data-testid="concierge-admin-tab">
          <div style={{ marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", margin: "0 0 4px" }}>Concierge Widget</h3>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
              Configure the smart conversational enquiry assistant, manage conversation flows, and view analytics.
            </p>
          </div>
          <ConciergeAdminPanel />
        </div>
      )}

      {/* CRM-UX-01 Stage 04: Terminology & Labels */}
      {tab === "terminology" && <TerminologyPanel />}
    </div>
  );
}

// ─── User State Badge ───────────────────────────────────────────────────────
function UserStateBadge({ state }) {
  const map = {
    ACTIVE:   "bg-emerald-50 border-emerald-200 text-emerald-700",
    INVITED:  "bg-amber-50 border-amber-200 text-amber-700",
    LOCKED:   "bg-rose-50 border-rose-200 text-rose-700",
    DISABLED: "bg-[#F3F3F3] border-[#C9C9C9] text-[#706E6B]",
  };
  const label = state || "ACTIVE";
  return (
    <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[label] || map.ACTIVE}`}>
      {label}
    </span>
  );
}

// ─── Invite User Modal ──────────────────────────────────────────────────────
function InviteUserModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ email: "", name: "", role: "EVENT_MANAGER" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await authApi.inviteUser(form);
      toast.success(`${form.name} invited. Share the activation link.`);
      onSuccess(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite user");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-2xl shadow-2xl p-6" data-testid="invite-user-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-[#181818] font-sans">Invite User</h3>
            <p className="text-xs text-[#706E6B] mt-0.5">Creates a secure activation link for the new user.</p>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="invite-user-form">
          {[["email","Email Address","user@example.com"],["name","Full Name","Jane Smith"]].map(([k,l,p]) => (
            <div key={k}>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{l} *</label>
              <input data-testid={`invite-${k}-input`} type={k === "email" ? "email" : "text"} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} placeholder={p} required
                className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
            </div>
          ))}
          <div>
            <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">Role *</label>
            <select data-testid="invite-role-select" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}
              className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3] transition-colors">
              <optgroup label="Business Roles">
                <option value="BUSINESS_OWNER">Business Owner</option>
                <option value="CRM_MANAGER">Sales Manager</option>
                <option value="RELATIONSHIP_MANAGER">Client Relationship Manager</option>
                <option value="EVENT_MANAGER">Event Manager</option>
                <option value="OPERATIONS_COORDINATOR">Operations Coordinator</option>
                <option value="FINANCE_APPROVER">Finance Manager</option>
                <option value="TENANT_ADMIN">System Administrator</option>
                <option value="PRASHASTA_E2E_COORDINATOR">E2E Coordinator</option>
              </optgroup>
              <optgroup label="Internal / Platform">
                <option value="SETUP_ADMIN">Setup Admin</option>
              </optgroup>
            </select>
          </div>
          <button data-testid="invite-submit-btn" type="submit" disabled={loading}
            className="w-full py-2.5 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send Invitation"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Activation Link Modal (shown after invite) ─────────────────────────────
function ActivationLinkModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result.activation_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success("Activation link copied to clipboard");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-white border border-[#E5E5E5] rounded-2xl shadow-2xl p-6" data-testid="activation-link-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <h3 className="text-base font-semibold text-[#181818]">User Invited</h3>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        <div className="bg-[#F3F3F3] rounded-xl p-4 mb-4 space-y-2 text-sm border border-[#E5E5E5]">
          <div className="flex justify-between"><span className="text-[#706E6B]">Name</span><span className="font-medium text-[#181818]">{result.name}</span></div>
          <div className="flex justify-between"><span className="text-[#706E6B]">Email</span><span className="font-medium text-[#181818]">{result.email}</span></div>
          <div className="flex justify-between"><span className="text-[#706E6B]">Role</span><span className="font-medium text-[#0176D3]">{result.role}</span></div>
          <div className="flex justify-between"><span className="text-[#706E6B]">Expires</span><span className="font-medium text-[#181818]">{new Date(result.expires_at).toLocaleDateString()}</span></div>
        </div>
        <p className="text-xs text-[#706E6B] mb-2 font-mono uppercase tracking-widest">Activation Link — Share with user</p>
        <div className="flex gap-2">
          <input data-testid="activation-url-field" readOnly value={result.activation_url}
            className="flex-1 px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-xs font-mono focus:outline-none" />
          <button data-testid="copy-activation-link-btn" onClick={copy}
            className="px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 flex items-center gap-1.5 transition-opacity">
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-xs text-[#706E6B] mt-3">
          Share this link with {result.email}. They must set their password before it expires.
        </p>
        <button onClick={onClose} className="mt-4 w-full py-2 rounded-lg border border-[#E5E5E5] text-sm text-[#706E6B] hover:bg-[#F3F3F3] transition-colors">Done</button>
      </div>
    </div>
  );
}

function ScopePill({ scope }) {
  const styles = {
    platform:     "bg-purple-50 border-purple-200 text-purple-700",
    tenant:       "bg-[#EEF4FF] border-[#0176D3]/30 text-[#0176D3]",
    "cross-tenant": "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[scope] || styles.tenant}`}>
      {scope}
    </span>
  );
}

// ─── CRM-UX-01 Stage 04: Terminology & Labels Panel ───────────────────────────

const SCOPE_GROUPS = {
  "crm.module":     "CRM Module Names",
  "crm.field":      "CRM Field Labels",
  "crm.leads.col":  "Leads Column Headers",
  "crm.opps.col":   "Opportunities Column Headers",
  "nav":            "Navigation Labels",
  "role":           "Role Display Labels",
};

function TerminologyPanel() {
  const { refresh: refreshLabels } = useLabelContext();
  const [labels, setLabels]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [scopeFilter, setScopeFilter] = useState("");
  const [editKey, setEditKey]       = useState(null);
  const [editValue, setEditValue]   = useState("");
  const [saving, setSaving]         = useState(false);
  const [search, setSearch]         = useState("");

  const loadLabels = async () => {
    setLoading(true);
    try {
      const { data } = await labelApi.list(scopeFilter ? { scope: scopeFilter } : undefined);
      setLabels(data?.labels || []);
    } catch {
      toast.error("Could not load label configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLabels(); }, [scopeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (label) => {
    setEditKey(label.label_key);
    setEditValue(label.display_value);
  };

  const cancelEdit = () => {
    setEditKey(null);
    setEditValue("");
  };

  const saveEdit = async (labelKey) => {
    if (!editValue.trim()) { toast.error("Label cannot be blank"); return; }
    setSaving(true);
    try {
      await labelApi.upsert(labelKey, { display_value: editValue.trim() });
      toast.success("Label updated");
      await loadLabels();
      await refreshLabels();
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update label");
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = async (labelKey) => {
    setSaving(true);
    try {
      await labelApi.remove(labelKey);
      toast.success("Restored to platform default");
      await loadLabels();
      await refreshLabels();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("No tenant override")) {
        toast("Already using platform default");
      } else {
        toast.error("Failed to restore default");
      }
    } finally {
      setSaving(false);
    }
  };

  const filtered = labels.filter(l =>
    !search ||
    l.label_key.toLowerCase().includes(search.toLowerCase()) ||
    l.display_value.toLowerCase().includes(search.toLowerCase()) ||
    (l.description || "").toLowerCase().includes(search.toLowerCase())
  );

  // Group by scope
  const grouped = {};
  filtered.forEach(l => {
    const group = SCOPE_GROUPS[l.scope] || l.scope || "Other";
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(l);
  });

  return (
    <div data-testid="terminology-admin-tab" className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#181818]">Terminology & Labels</h3>
          <p className="text-sm text-[#706E6B] mt-0.5">
            Customize display labels for CRM modules, fields and navigation. Underlying database keys, API contracts and status codes are never changed.
          </p>
        </div>
        <button onClick={loadLabels} className="p-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search labels…"
            className="w-full pl-8 pr-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={e => setScopeFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30"
        >
          <option value="">All Scopes</option>
          {Object.entries(SCOPE_GROUPS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-[#F3F3F3] rounded-lg animate-pulse" />)}</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([groupName, items]) => (
            <div key={groupName}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#706E6B] mb-2">{groupName}</p>
              <div className="rounded-xl border border-[#E5E5E5] overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono text-[#706E6B] uppercase tracking-wider">Key</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono text-[#706E6B] uppercase tracking-wider">Current Label</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono text-[#706E6B] uppercase tracking-wider hidden md:table-cell">Description</th>
                      <th className="px-4 py-2.5 text-[10px] font-mono text-[#706E6B] uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F3F3]">
                    {items.map(label => (
                      <tr key={label.label_key} className="bg-white hover:bg-[#F9F9F9]">
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-mono text-[#706E6B]">{label.label_key}</span>
                        </td>
                        <td className="px-4 py-3">
                          {editKey === label.label_key ? (
                            <input
                              data-testid={`label-edit-input-${label.label_key.replace(/\./g, "-")}`}
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") saveEdit(label.label_key); if (e.key === "Escape") cancelEdit(); }}
                              autoFocus
                              className="w-full px-2 py-1 border border-[#0176D3]/30 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30"
                            />
                          ) : (
                            <span className="text-[#181818] font-medium">{label.display_value}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs text-[#706E6B]">{label.description || "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {label.is_tenant_override ? (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#EEF4FF] border border-[#0176D3]/20 text-[#0176D3]">CUSTOMIZED</span>
                          ) : (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#F3F3F3] border border-[#E5E5E5] text-[#706E6B]">DEFAULT</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 justify-end">
                            {editKey === label.label_key ? (
                              <>
                                <button
                                  data-testid={`label-save-${label.label_key.replace(/\./g, "-")}`}
                                  disabled={saving}
                                  onClick={() => saveEdit(label.label_key)}
                                  className="px-2.5 py-1 bg-[#EEF4FF] border border-[#0176D3]/30 rounded text-xs text-[#0176D3] font-medium hover:bg-[#DFF0FF] disabled:opacity-60"
                                >
                                  {saving ? "…" : "Save"}
                                </button>
                                <button onClick={cancelEdit} className="px-2.5 py-1 border border-[#E5E5E5] rounded text-xs text-[#706E6B] hover:border-slate-500">
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  data-testid={`label-edit-btn-${label.label_key.replace(/\./g, "-")}`}
                                  onClick={() => startEdit(label)}
                                  className="p-1.5 rounded border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/40 transition-colors"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                                {label.is_tenant_override && (
                                  <button
                                    data-testid={`label-restore-${label.label_key.replace(/\./g, "-")}`}
                                    onClick={() => restoreDefault(label.label_key)}
                                    disabled={saving}
                                    className="p-1.5 rounded border border-[#E5E5E5] text-[#706E6B] hover:text-rose-500 hover:border-rose-300 transition-colors disabled:opacity-40"
                                    title="Restore platform default"
                                  >
                                    <RotateCcw className="w-3 h-3" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <Empty msg="No labels found matching your search." />}
        </div>
      )}
    </div>
  );
}


function RiskPill({ risk }) {
  const styles = {
    LOW:           "bg-green-50 border-green-200 text-green-700",
    MEDIUM:        "bg-amber-50 border-amber-200 text-amber-700",
    HIGH:          "bg-red-50 border-red-200 text-red-700",
    PLATFORM_ONLY: "bg-purple-50 border-purple-200 text-purple-700",
  };
  return (
    <span className={`flex-shrink-0 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[risk] || styles.LOW}`}>
      {risk}
    </span>
  );
}

function Empty({ msg }) {
  return <div className="text-center py-8 text-sm text-[#706E6B] bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">{msg}</div>;
}
